import { Globe2, Languages, Loader2, Lock, LogIn, OctagonAlert, TriangleAlert, Zap } from 'lucide-react';
import type { TFunction } from 'i18next';
import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router';
import { useSearchParams } from '@/lib/scopedParams';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api, isApiError, type LoginResponse } from '../api';
import { clearSession, lastSessionEnd, setSession, useSession } from '../api/session';
import { Badge } from '../components/Badge';
import { useDocumentTitle } from '../components/PageHeader';
import { TestSystemBanner, useDeployment } from '../components/layout/TestSystemBanner';
import { USE_MOCKS } from '../config/env';
import { LANGUAGES, setLanguage, useLanguage } from '../i18n';

/** Only same-app paths are accepted as a post-login destination. */
function safeNext(raw: string | null): string {
  if (!raw || !raw.startsWith('/') || raw.startsWith('//') || raw.startsWith('/login')) return '/';
  return raw;
}

function describeLoginError(error: unknown, t: TFunction<'common'>): { title: string; body: string } {
  if (!isApiError(error)) return { title: t('login.failed'), body: error instanceof Error ? error.message : t('login.requestFailed') };
  switch (error.code) {
    case 'INVALID_CREDENTIALS':
      return { title: t('login.invalidTitle'), body: t('login.invalidBody') };
    case 'ACCOUNT_LOCKED':
      return { title: t('login.lockedTitle'), body: t('login.lockedBody') };
    case 'NETWORK_ERROR':
      return { title: t('login.networkTitle'), body: t('login.networkBody') };
    case 'RATE_LIMITED':
      return { title: t('login.failed'), body: t('login.demoLimited') };
    default:
      return { title: t('login.failed'), body: t('login.otherBody', { message: error.message, code: error.code, requestId: error.requestId }) };
  }
}

function LanguageSwitch() {
  const { t } = useTranslation();
  const lang = useLanguage();
  return (
    <div className="inline-flex items-center gap-1 rounded-md border bg-card p-0.5 text-xs shadow-xs" role="group" aria-label={t('lang.label')}>
      <Languages className="mx-1 size-3.5 text-muted-foreground" aria-hidden="true" />
      {LANGUAGES.map((l) => (
        <button
          key={l}
          type="button"
          lang={l}
          aria-pressed={lang === l}
          onClick={() => setLanguage(l)}
          className="rounded-sm px-2 py-1 font-medium text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 aria-pressed:bg-primary aria-pressed:text-primary-foreground"
        >
          {t(`lang.${l}`)}
        </button>
      ))}
    </div>
  );
}

/** `/login`: staff sign-in against `POST /admin/v1/auth/login` (password + TOTP). */
export function LoginPage() {
  const { t } = useTranslation();
  const deployment = useDeployment();
  useDocumentTitle(t('login.docTitle'));
  const session = useSession();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = safeNext(params.get('next'));
  const wantsMfa = params.get('mfa') === '1';
  const [email, setEmail] = useState(session?.email ?? '');
  const [password, setPassword] = useState('');
  const [totp, setTotp] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [ended] = useState(lastSessionEnd);
  // The OT&E quick sign-in's account, when the server offers it (`GET /auth/demo-login`).
  const [demoEmail, setDemoEmail] = useState<string | null>(null);
  const id = useId();
  const totpRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const ctl = new AbortController();
    api.demoLoginStatus({ signal: ctl.signal }).then(
      (d) => setDemoEmail(d.email),
      () => setDemoEmail(null),
    );
    return () => ctl.abort();
  }, []);

  // Re-authenticating for MFA: drop the current (non-MFA) session so this page is shown.
  useEffect(() => {
    if (wantsMfa && session && !session.mfa_verified) clearSession('logout');
  }, [wantsMfa, session]);

  useEffect(() => {
    if (wantsMfa) totpRef.current?.focus();
  }, [wantsMfa]);

  if (session && !(wantsMfa && !session.mfa_verified)) return <Navigate to={next} replace />;

  const enter = (res: LoginResponse, signedInAs: string) => {
    setSession({
      token: res.token,
      email: signedInAs,
      mfa_verified: res.mfa_verified,
      expires_at: new Date(Date.now() + res.expires_in_seconds * 1000).toISOString(),
    });
    navigate(next, { replace: true });
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const code = totp.replace(/\s+/g, '');
      const res = await api.login({ email: email.trim(), password, ...(code ? { totp: code } : {}) });
      setPassword('');
      setTotp('');
      enter(res, email.trim());
    } catch (err) {
      setError(err);
      setPassword('');
      setTotp('');
    } finally {
      setBusy(false);
    }
  };

  const quickSignIn = async () => {
    if (!demoEmail) return;
    setBusy(true);
    setError(null);
    try {
      enter(await api.demoLogin(), demoEmail);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const problem = error ? describeLoginError(error, t) : null;
  const credentialsWrong = isApiError(error) && error.code === 'INVALID_CREDENTIALS';

  return (
    <>
      <TestSystemBanner className="fixed inset-x-0 top-0 z-30" />
      <main id="main" className="flex min-h-svh flex-col items-center justify-center gap-6 bg-muted/40 px-4 py-10">
        <div className="flex w-full max-w-md justify-end">
          <LanguageSwitch />
        </div>
        <Card className="w-full max-w-md shadow-sm">
          <form onSubmit={submit} aria-labelledby={`${id}-title`} className="grid gap-6">
            <CardHeader className="gap-3">
              <div className="flex items-start gap-3">
                <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                  <Globe2 className="size-5" aria-hidden="true" />
                </div>
                <div className="min-w-0 flex-1 flex flex-col gap-1">
                  <CardTitle id={`${id}-title`} className="text-lg leading-tight">
                    {t('app.name')}
                  </CardTitle>
                  <CardDescription>{t('login.subtitle')}</CardDescription>
                </div>
                {deployment ? (
                  <Badge tone={deployment.environment === 'production' ? 'crit' : 'warn'} icon={false} title={t('deployment.label', { env: t(`deployment.${deployment.environment}`) })}>
                    {t(`deployment.${deployment.environment}`)}
                  </Badge>
                ) : null}
              </div>
              <p className="text-sm text-muted-foreground">{t('login.intro')}</p>
            </CardHeader>

            <CardContent className="grid gap-4">
              {ended === 'expired' && !problem ? (
                <p className="flex items-start gap-2 rounded-md border border-warning/30 bg-warning-soft px-3 py-2 text-sm" role="status">
                  <TriangleAlert className="mt-0.5 size-4 shrink-0 text-warning" aria-hidden="true" />
                  {t('login.expired')}
                </p>
              ) : null}
              {wantsMfa ? (
                <p className="flex items-start gap-2 rounded-md border border-info/25 bg-info-soft px-3 py-2 text-sm" role="status">
                  <Lock className="mt-0.5 size-4 shrink-0 text-info" aria-hidden="true" />
                  {t('login.needsMfa')}
                </p>
              ) : null}

              <div className="grid gap-1.5">
                <Label htmlFor={`${id}-email`}>{t('login.email')}</Label>
                <Input
                  id={`${id}-email`}
                  type="email"
                  autoComplete="username"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={busy}
                  aria-invalid={credentialsWrong || undefined}
                  placeholder="name@company.vn"
                />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor={`${id}-password`}>{t('login.password')}</Label>
                <Input
                  id={`${id}-password`}
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={busy}
                  aria-invalid={credentialsWrong || undefined}
                />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor={`${id}-totp`}>{t('login.totp')}</Label>
                <Input
                  id={`${id}-totp`}
                  ref={totpRef}
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9 ]{6,7}"
                  maxLength={7}
                  placeholder="123 456"
                  value={totp}
                  onChange={(e) => setTotp(e.target.value)}
                  disabled={busy}
                  aria-describedby={`${id}-totp-hint`}
                  aria-invalid={credentialsWrong || undefined}
                  className="font-mono tracking-widest"
                />
                <p id={`${id}-totp-hint`} className="text-xs text-muted-foreground">
                  {t('login.totpHint')}
                </p>
              </div>

              {problem ? (
                <div className="flex gap-3 rounded-md border border-critical/30 bg-critical-soft p-3 text-sm" role="alert">
                  <OctagonAlert className="mt-0.5 size-4 shrink-0 text-critical" aria-hidden="true" />
                  <div className="flex flex-col gap-1">
                    <strong className="font-semibold">{problem.title}</strong>
                    <p className="text-foreground/85">{problem.body}</p>
                  </div>
                </div>
              ) : null}

              <Button type="submit" className="w-full" disabled={busy}>
                {busy ? <Loader2 className="animate-spin" aria-hidden="true" /> : <LogIn aria-hidden="true" />}
                {busy ? t('login.submitting') : t('login.submit')}
              </Button>

              {demoEmail ? (
                <div className="grid gap-1.5 border-t pt-4">
                  <Button type="button" variant="outline" className="w-full" disabled={busy} onClick={quickSignIn} aria-describedby={`${id}-demo-hint`}>
                    <Zap aria-hidden="true" />
                    {t('login.demo')}
                  </Button>
                  <p id={`${id}-demo-hint`} className="text-xs text-muted-foreground">
                    {t('login.demoHint', { email: demoEmail })}
                  </p>
                </div>
              ) : null}
            </CardContent>

            <CardFooter className="flex-col items-start gap-2 border-t pt-4 text-xs text-muted-foreground">
              <p>{t('login.footer')}</p>
              {USE_MOCKS ? (
                <p>
                  {t('login.mock', { locked: '“locked”' })}
                </p>
              ) : null}
            </CardFooter>
          </form>
        </Card>
      </main>
    </>
  );
}
